import { FingerType, CourseDay, CourseRound } from '@/types';

/**
 * EXACT 30-DAY TRAINING COURSE - Based on Provided Training Schedule
 * 
 * Round Pattern:
 * - Round 1: L, 1    (Left hand, complexity 1)
 * - Round 2: R, 1    (Right hand, complexity 1)
 * - Round 3: LR 1    (Both hands, complexity 1)
 * - Round 4: LR 1-3  (Both hands, complexity 1-3)
 * - Round 5: LR 1-5  (Both hands, complexity 1-5)
 */

const ROUND_DURATION = 120000; // 2 minutes in milliseconds

/**
 * Training schedule with exact finger combinations for each day
 */
const trainingSchedule = {
  1: ["Pinky"],
  2: ["RingFinger"],
  3: ["Middle"],
  4: ["RingFinger", "Pinky"],
  5: ["Index"],
  6: ["Index", "Middle"],
  7: ["Middle", "Pinky"],
  8: ["Thumb"],
  9: ["Index", "RingFinger"],
  10: ["Thumb", "Pinky"],
  11: ["Middle", "RingFinger"],
  12: ["Thumb", "Middle"],
  13: ["Thumb", "Index"],
  14: ["Index", "RingFinger", "Pinky"],
  15: ["Thumb", "RingFinger", "Middle"],
  16: ["Index", "Thumb", "Pinky"],
  17: ["Index", "RingFinger", "Pinky"],
  18: ["Thumb", "Index", "RingFinger"],
  19: ["Index", "RingFinger", "Pinky"],
  20: ["Thumb", "RingFinger", "Pinky"],
  21: ["Thumb", "Index", "Middle"],
  22: ["Index", "Middle", "RingFinger", "Pinky"],
  23: ["Thumb", "Index", "Middle", "RingFinger"],
  24: ["Thumb", "Middle", "RingFinger", "Pinky"],
  25: ["Thumb", "Index", "Middle", "Pinky"],
  26: ["Thumb", "Index", "RingFinger", "Pinky"],
  27: ["Thumb", "Index", "Middle", "RingFinger", "Pinky"],
  28: ["Thumb", "Index", "Middle", "RingFinger", "Pinky"],
  29: ["Thumb", "Index", "Middle", "RingFinger", "Pinky"],
  30: ["Thumb", "Index", "Middle", "RingFinger", "Pinky"]
};

/**
 * Convert finger names to FingerType arrays
 */
const convertToFingerTypes = (fingerNames: string[]): { left: FingerType[], right: FingerType[] } => {
  const left: FingerType[] = [];
  const right: FingerType[] = [];

  fingerNames.forEach(finger => {
    switch (finger) {
      case "Pinky":
        left.push('leftPinky');
        right.push('rightPinky');
        break;
      case "RingFinger":
        left.push('leftRing');
        right.push('rightRing');
        break;
      case "Middle":
        left.push('leftMiddle');
        right.push('rightMiddle');
        break;
      case "Index":
        left.push('leftPointing');
        right.push('rightPointing');
        break;
      case "Thumb":
        left.push('leftThumb');
        right.push('rightThumb');
        break;
    }
  });

  return { left, right };
};

/**
 * Helper function to create course round with exact pattern
 */
const createCourseRound = (
  roundNumber: number,
  hands: 'left' | 'right' | 'both',
  complexityMin: number,
  complexityMax: number,
  activeFingersLeft: FingerType[],
  activeFingersRight: FingerType[],
  description: string
): CourseRound => ({
  roundNumber,
  completed: false,
  fingerConfig: {
    activeFingersLeft: hands === 'right' ? [] : activeFingersLeft,
    activeFingersRight: hands === 'left' ? [] : activeFingersRight,
    letterComplexity: { min: complexityMin, max: complexityMax }
  },
  description,
  duration: ROUND_DURATION
});

/**
 * Helper function to create a full day with standard 5-round pattern
 */
const createTrainingDay = (
  dayNumber: number,
  fingerNames: string[]
): CourseDay => {
  const fingers = convertToFingerTypes(fingerNames);
  const fingerDesc = fingerNames.join(' + ');

  return {
    dayNumber,
    unlocked: dayNumber === 1, // Only day 1 starts unlocked
    completed: false,
    rounds: [
      createCourseRound(1, 'left', 1, 1, fingers.left, fingers.right, `L, 1 - ${fingerDesc}`),
      createCourseRound(2, 'right', 1, 1, fingers.left, fingers.right, `R, 1 - ${fingerDesc}`),
      createCourseRound(3, 'both', 1, 1, fingers.left, fingers.right, `LR 1 - ${fingerDesc}`),
      createCourseRound(4, 'both', 1, 3, fingers.left, fingers.right, `LR 1-3 - ${fingerDesc}`),
      createCourseRound(5, 'both', 1, 5, fingers.left, fingers.right, `LR 1-5 - ${fingerDesc}`)
    ]
  };
};

/**
 * EXACT 30-DAY COURSE STRUCTURE FROM PROVIDED SCHEDULE
 */
export const COMPLETE_TRAINING_COURSE: CourseDay[] = Object.entries(trainingSchedule).map(([day, fingers]) =>
  createTrainingDay(parseInt(day), fingers)
);

export const LEVEL_2_COURSE: CourseDay[] = COMPLETE_TRAINING_COURSE.map(day => ({
  ...day,
  unlocked: false, // Locked initially
  rounds: day.rounds.map(r => ({ ...r, unlocked: false, description: `[Level 2] ` + r.description }))
}));

export const COURSE_LEVELS: Record<number, CourseDay[]> = {
  1: COMPLETE_TRAINING_COURSE,
  2: LEVEL_2_COURSE
};

/**
 * Helper functions for course management
 */
export const getCourseDay = (levelNumber: number, dayNumber: number): CourseDay | null => {
  const course = COURSE_LEVELS[levelNumber] || COURSE_LEVELS[1];
  return course.find(day => day.dayNumber === dayNumber) || null;
};

export const getCourseRound = (levelNumber: number, dayNumber: number, roundNumber: number): CourseRound | null => {
  const day = getCourseDay(levelNumber, dayNumber);
  return day?.rounds.find(round => round.roundNumber === roundNumber) || null;
};

export const getTotalCourseDays = (levelNumber: number = 1): number => {
  const course = COURSE_LEVELS[levelNumber] || COURSE_LEVELS[1];
  return course.length;
};

export const getTotalCourseRounds = (levelNumber: number = 1): number => {
  const course = COURSE_LEVELS[levelNumber] || COURSE_LEVELS[1];
  return course.reduce((total, day) => total + day.rounds.length, 0);
};

/**
 * Progress calculation utilities
 */
export const calculateDayProgress = (day: CourseDay): number => {
  const completedRounds = day.rounds.filter(round => round.completed).length;
  return (completedRounds / day.rounds.length) * 100;
};

export const calculateCourseProgress = (course: CourseDay[]): number => {
  const completedDays = course.filter(day => day.completed).length;
  return (completedDays / course.length) * 100;
};

/**
 * Unlocking logic
 */
export const isDayUnlockable = (dayNumber: number, courseProgress: CourseDay[]): boolean => {
  if (dayNumber === 1) return true; // Day 1 always unlocked

  const previousDay = courseProgress.find(day => day.dayNumber === dayNumber - 1);
  return previousDay?.completed || false;
};

export const isRoundUnlockable = (roundNumber: number, dayRounds: CourseRound[]): boolean => {
  if (roundNumber === 1) return true; // Round 1 always unlocked when day is unlocked

  const previousRound = dayRounds.find(round => round.roundNumber === roundNumber - 1);
  return previousRound?.completed || false;
};

/**
 * Validation helper - ensures course structure matches provided schedule
 */
export const validateCourseStructure = (): boolean => {
  console.log('🔍 Validating course structure...');

  // Check total days
  if (COMPLETE_TRAINING_COURSE.length !== 30) {
    console.error(`❌ Expected 30 days, got ${COMPLETE_TRAINING_COURSE.length}`);
    return false;
  }

  // Check each day has 5 rounds
  for (const day of COMPLETE_TRAINING_COURSE) {
    if (day.rounds.length !== 5) {
      console.error(`❌ Day ${day.dayNumber} has ${day.rounds.length} rounds, expected 5`);
      return false;
    }

    // Check round pattern: L,1 → R,1 → LR,1 → LR,1-3 → LR,1-5
    const expectedPattern = [
      { hands: 'left', minComp: 1, maxComp: 1 },
      { hands: 'right', minComp: 1, maxComp: 1 },
      { hands: 'both', minComp: 1, maxComp: 1 },
      { hands: 'both', minComp: 1, maxComp: 3 },
      { hands: 'both', minComp: 1, maxComp: 5 }
    ];

    for (let i = 0; i < 5; i++) {
      const round = day.rounds[i];
      const expected = expectedPattern[i];
      const actualHands = round.fingerConfig.activeFingersLeft.length > 0 && round.fingerConfig.activeFingersRight.length > 0 ? 'both' :
        round.fingerConfig.activeFingersLeft.length > 0 ? 'left' : 'right';

      if (actualHands !== expected.hands) {
        console.error(`❌ Day ${day.dayNumber} Round ${i + 1}: Expected ${expected.hands}, got ${actualHands}`);
        return false;
      }

      if (round.fingerConfig.letterComplexity.min !== expected.minComp ||
        round.fingerConfig.letterComplexity.max !== expected.maxComp) {
        console.error(`❌ Day ${day.dayNumber} Round ${i + 1}: Expected complexity ${expected.minComp}-${expected.maxComp}, got ${round.fingerConfig.letterComplexity.min}-${round.fingerConfig.letterComplexity.max}`);
        return false;
      }
    }
  }

  console.log('✅ Course structure validation passed!');
  return true;
};

/**
 * Debug helper - prints course structure overview
 */
export const printCourseOverview = (): void => {
  console.log('📚 30-Day Course Overview:');

  COMPLETE_TRAINING_COURSE.forEach(day => {
    const scheduledFingers = trainingSchedule[day.dayNumber as keyof typeof trainingSchedule];
    console.log(`Day ${day.dayNumber.toString().padStart(2)}: ${scheduledFingers.join(' + ')} - Pattern: L,1 → R,1 → LR,1 → LR,1-3 → LR,1-5`);
  });

  console.log(`\n📊 Total: ${getTotalCourseDays()} days, ${getTotalCourseRounds()} rounds`);
};

// Validate structure on import
if (typeof window !== 'undefined') {
  validateCourseStructure();
}